/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import type { ReactNode } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import { spacing } from "../../../../../global/emotion";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { IconElement } from "../../icons/Icon/Icon";

const noticeStyle: Interpolation = {
  alignItems: "center",
  borderRadius: spacing.s,
  display: "flex",
  gap: spacing.s,
  marginBottom: spacing.m,
  padding: spacing.m,
  textAlign: "left",
  ":not(#__unused__) a": {
    textDecoration: "underline",
  },
};

const variantStyles: Record<
  NoticeProps["variant"],
  Interpolation<ThemeType>
> = {
  plain: (theme) => ({
    backgroundColor: theme.semanticColors.neutralsLowest,
    color: theme.semanticColors.primary,
  }),
  info: (theme) => ({
    backgroundColor: theme.semanticColors.accentLowest,
    color: theme.semanticColors.accentHigh,
  }),
  error: (theme) => ({
    backgroundColor: theme.semanticColors.errorLowest,
    color: theme.semanticColors.errorMedium,
  }),
};

export type NoticeProps = HallowElementProps<"div"> & {
  variant?: "plain" | "info" | "error";
  icon?: IconElement;
  body?: ReactNode;
};

export const Notice = ({
  variant = "info",
  icon,
  body,
  children,
  ...props
}: NoticeProps) => {
  return (
    <div css={[noticeStyle, variantStyles[variant]]} {...props}>
      {icon ?? null}
      <p className="notice-body">{body ?? children}</p>
    </div>
  );
};

Notice.displayName = "Notice";
